import React, { useState } from "react";
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  HStack,
  SimpleGrid,
  Button,
  Badge,
  Icon,
  Divider,
  useToast,
  Tooltip,
  Circle,
  Input,
  Switch,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Code,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Progress,
  Alert,
  AlertIcon,
  Wrap,
  WrapItem,
} from "@chakra-ui/react";
import {
  FaCopy,
  FaCheck,
  FaSun,
  FaCoins,
  FaHome,
  FaArrowRight,
  FaBook,
  FaPalette,
  FaFont,
  FaSquare,
  FaCircle,
  FaGraduationCap,
  FaHeart,
  FaLightbulb,
  FaEye,
  FaTint,
  FaLeaf,
  FaShieldAlt,
  FaLayerGroup,
} from "react-icons/fa";
import { brandConfig } from "../../brandConfig";
import { usePageTitle } from "../../hooks/useDocumentTitle";

// ─── Brand Tokens ────────────────────────────────────────────────────────────
const NAVY        = "#141C49";   // Master Navy — 50-60% of all materials
const NAVY_DARK   = "#0d1236";
const NAVY_OVR92  = "rgba(20,28,73,0.92)";
const NAVY_OVR88  = "rgba(20,28,73,0.88)";
const NAVY_OVR85  = "rgba(20,28,73,0.85)";

// One Solar Australia
const SOLAR       = "#C69214";   // Rich Solar Gold
const SOLAR_2     = "#F2B705";   // Warm Amber
const SOLAR_ACC   = "#B87333";   // Burnt Bronze
const SOLAR_DARK  = "#a07810";

// One Finance Australia
const FINANCE     = "#2F3A4A";   // Slate Graphite
const FINANCE_2   = "#5E6A75";   // Steel Grey
const FINANCE_ACC = "#C9CDD3";   // Platinum
const FINANCE_DARK= "#1e2830";

// One Property Australia
const PROP        = "#1F3D2B";   // Heritage Green
const PROP_2      = "#6C8F7A";   // Sage Growth
const PROP_ACC    = "#C2A878";   // Warm Sand
const PROP_DARK   = "#172e20";

// One Water Australia
const WATER       = "#6FAFD6";   // Mineral Blue
const WATER_2     = "#2C7DA0";   // Deep Aqua
const WATER_ACC   = "#EAF3FB";   // Ice Mist
const WATER_DARK  = "#5a9abf";

// ─── Shared Components ───────────────────────────────────────────────────────

const ColorSwatch: React.FC<{
  color: string;
  name: string;
  description?: string;
  size?: "sm" | "md" | "lg";
}> = ({ color, name, description, size = "md" }) => {
  const [copied, setCopied] = useState(false);
  const toast = useToast();

  const sizes = {
    sm: { box: "60px", text: "xs" as const },
    md: { box: "80px", text: "sm" as const },
    lg: { box: "100px", text: "md" as const },
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(color);
    setCopied(true);
    toast({ title: "Copied!", description: color, status: "success", duration: 2000, isClosable: true });
    setTimeout(() => setCopied(false), 2000);
  };

  const isLight = (hex: string) => {
    const c = hex.replace("#", "");
    if (c.length < 6) return false;
    const rgb = parseInt(c, 16);
    const r = (rgb >> 16) & 0xff;
    const g = (rgb >> 8) & 0xff;
    const b = rgb & 0xff;
    return 0.2126 * r + 0.7152 * g + 0.0722 * b > 128;
  };

  const textColor = color.startsWith("#") && isLight(color) ? "gray.800" : "white";

  return (
    <Tooltip label="Click to copy" placement="top">
      <VStack spacing={2} cursor="pointer" onClick={handleCopy} transition="all 0.2s" _hover={{ transform: "scale(1.05)" }}>
        <Box
          w={sizes[size].box}
          h={sizes[size].box}
          bg={color}
          borderRadius="xl"
          display="flex"
          alignItems="center"
          justifyContent="center"
          boxShadow="lg"
          border="2px solid"
          borderColor="gray.200"
          position="relative"
          overflow="hidden"
          _before={{
            content: '""',
            position: "absolute",
            top: 0, left: 0, right: 0, bottom: 0,
            background: "linear-gradient(135deg, rgba(255,255,255,0.18) 0%, transparent 50%)",
          }}
        >
          <Icon as={copied ? FaCheck : FaCopy} color={textColor} boxSize={4} opacity={0.8} />
        </Box>
        <VStack spacing={0}>
          <Text fontSize={sizes[size].text} fontWeight="600" color="gray.800">{name}</Text>
          <Code fontSize="xs" bg="gray.100" color="gray.600" px={2} borderRadius="md">{color}</Code>
          {description && <Text fontSize="xs" color="gray.500" textAlign="center" maxW="100px">{description}</Text>}
        </VStack>
      </VStack>
    </Tooltip>
  );
};

const SectionHeader: React.FC<{
  icon: React.ElementType;
  title: string;
  subtitle?: string;
  color?: string;
}> = ({ icon, title, subtitle, color = SOLAR }) => (
  <HStack spacing={4} mb={8}>
    <Circle size="50px" bg={color} boxShadow={`0 4px 20px ${color}50`}>
      <Icon as={icon} boxSize={6} color="white" />
    </Circle>
    <VStack align="start" spacing={0}>
      <Heading size="lg" color="gray.800">{title}</Heading>
      {subtitle && <Text color="gray.500" fontSize="sm">{subtitle}</Text>}
    </VStack>
  </HStack>
);

const LightCard: React.FC<{ children: React.ReactNode; p?: number }> = ({ children, p = 8 }) => (
  <Box bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200" p={p} boxShadow="0 4px 20px rgba(0,0,0,0.07)">
    {children}
  </Box>
);

const NavyDivider: React.FC<{ label: string }> = ({ label }) => (
  <Box bg={NAVY} borderRadius="xl" px={6} py={4} mb={6}>
    <Text color="white" fontWeight="700" fontSize="sm" textTransform="uppercase" letterSpacing="wider">{label}</Text>
  </Box>
);

// ─── Main Component ──────────────────────────────────────────────────────────

const BrandStyleGuideV2: React.FC = () => {
  usePageTitle("Brand Style Guide v2 — One Group Australia");
  const toast = useToast();

  return (
    <>

      {/* ── Hero ── */}
      <Box
        position="relative"
        py={20}
        overflow="hidden"
        backgroundImage="url('https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920&q=80')"
        backgroundSize="cover"
        backgroundPosition="center"
        backgroundAttachment="fixed"
        _before={{
          content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          bg: NAVY_OVR92,
        }}
        _after={{
          content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
          background: `radial-gradient(circle at 30% 20%, rgba(198,146,20,0.14) 0%, transparent 40%), radial-gradient(circle at 70% 80%, rgba(31,61,43,0.12) 0%, transparent 40%)`,
        }}
      >
        <Container maxW="container.xl" position="relative" zIndex={1}>
          <VStack spacing={6} textAlign="center">
            <Badge
              bg={`linear-gradient(135deg, ${SOLAR} 0%, ${SOLAR_ACC} 100%)`}
              color="white" px={4} py={2} borderRadius="full" fontSize="sm"
              textTransform="uppercase" letterSpacing="wider"
              boxShadow={`0 4px 15px ${SOLAR}50`}
            >
              Brand & Prosperity Engine Manual 2026 — v1.3
            </Badge>
            <Heading size="3xl" bgGradient="linear(to-r, white, gray.300)" bgClip="text" fontWeight="black">
              One Group Australia
            </Heading>
            <Text fontSize="xl" color="gray.300" maxW="2xl">
              Building{" "}
              <Text as="span" color={SOLAR} fontWeight="600">The Prosperity Engine™</Text>
              {" "}— From Health to Wealth
            </Text>
            <Text fontSize="md" color="gray.400" maxW="3xl" fontStyle="italic">
              Structured · Calm · Intentional · Long-term
            </Text>
            <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8} pt={8}>
              {[
                { label: "Master Navy", value: NAVY },
                { label: "Division Colours", value: "4" },
                { label: "Typography Stacks", value: "2" },
                { label: "Brand Archetypes", value: "4" },
              ].map((stat, idx) => (
                <VStack key={idx}>
                  <Text fontSize="xl" fontWeight="bold" color={SOLAR}>{stat.value}</Text>
                  <Text fontSize="sm" color="gray.400">{stat.label}</Text>
                </VStack>
              ))}
            </SimpleGrid>
          </VStack>
        </Container>
      </Box>

      {/* ── Tabs ── */}
      <Container maxW="container.xl" py={12}>
        <Tabs variant="soft-rounded" colorScheme="gray">
          <TabList
            bg="white" border="1px solid" borderColor="gray.200" p={2} borderRadius="2xl"
            flexWrap="wrap" justifyContent="center" boxShadow="sm"
          >
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaPalette} mr={2} />Colours</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaFont} mr={2} />Typography</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaSquare} mr={2} />Components</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaLayerGroup} mr={2} />Engine</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaGraduationCap} mr={2} />Archetypes</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: NAVY }}><Icon as={FaBook} mr={2} />Voice & Rules</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: SOLAR }}><Icon as={FaSun} mr={2} />Solar</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: FINANCE }}><Icon as={FaCoins} mr={2} />Finance</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: PROP }}><Icon as={FaHome} mr={2} />Property</Tab>
            <Tab color="gray.500" _selected={{ color: "white", bg: WATER }}><Icon as={FaTint} mr={2} />Water</Tab>
          </TabList>

          <TabPanels mt={8}>

            {/* ══════════════════════════════════════════
                TAB 1 — COLOURS
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">

                {/* Foundation */}
                <LightCard>
                  <SectionHeader icon={FaCircle} title="Foundation Colour — Master Navy" subtitle="50–60% of all brand materials" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                    <ColorSwatch color={NAVY}      name="Master Navy"  description="Primary anchor"  size="lg" />
                    <ColorSwatch color={NAVY_DARK} name="Navy Dark"    description="Hover / depth"   size="lg" />
                    <ColorSwatch color="#FFFFFF"   name="White"        description="Page backgrounds" size="lg" />
                  </SimpleGrid>
                  <Alert status="info" mt={6} borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.200">
                    <AlertIcon />
                    <Text fontSize="sm" color="gray.700">
                      <strong>Colour Ratio Rule:</strong> 60% Navy · 30% Division Colour · 10% Accent. Navy dominates every asset.
                    </Text>
                  </Alert>
                </LightCard>

                {/* Solar */}
                <LightCard>
                  <SectionHeader icon={FaSun} title="One Solar Australia" subtitle="Signals power and value" color={SOLAR} />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    <ColorSwatch color={SOLAR}     name="Rich Solar Gold" description="Primary" />
                    <ColorSwatch color={SOLAR_2}   name="Warm Amber"      description="Secondary" />
                    <ColorSwatch color={SOLAR_ACC} name="Burnt Bronze"    description="Accent" />
                  </SimpleGrid>
                </LightCard>

                {/* Finance */}
                <LightCard>
                  <SectionHeader icon={FaCoins} title="One Finance Australia" subtitle="Signals structure and discipline" color={FINANCE} />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    <ColorSwatch color={FINANCE}     name="Slate Graphite" description="Primary" />
                    <ColorSwatch color={FINANCE_2}   name="Steel Grey"     description="Secondary" />
                    <ColorSwatch color={FINANCE_ACC} name="Platinum"       description="Accent" />
                  </SimpleGrid>
                </LightCard>

                {/* Property */}
                <LightCard>
                  <SectionHeader icon={FaHome} title="One Property Australia" subtitle="Signals growth and stability" color={PROP} />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    <ColorSwatch color={PROP}     name="Heritage Green" description="Primary" />
                    <ColorSwatch color={PROP_2}   name="Sage Growth"    description="Secondary" />
                    <ColorSwatch color={PROP_ACC} name="Warm Sand"      description="Accent" />
                  </SimpleGrid>
                </LightCard>

                {/* Water */}
                <LightCard>
                  <SectionHeader icon={FaTint} title="One Water Australia" subtitle="Signals clarity and purity" color={WATER} />
                  <SimpleGrid columns={{ base: 3, md: 3 }} spacing={8}>
                    <ColorSwatch color={WATER}     name="Mineral Blue" description="Primary" />
                    <ColorSwatch color={WATER_2}   name="Deep Aqua"    description="Accent" />
                    <ColorSwatch color={WATER_ACC} name="Ice Mist"     description="Light BG" />
                  </SimpleGrid>
                </LightCard>

                {/* Status */}
                <LightCard>
                  <SectionHeader icon={FaEye} title="Status Colours" subtitle="System feedback and state indicators" color={NAVY} />
                  <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                    <ColorSwatch color="#30D158" name="Success"  description="Confirmations" />
                    <ColorSwatch color="#FF9F0A" name="Warning"  description="Caution" />
                    <ColorSwatch color="#FF3B30" name="Error"    description="Errors" />
                    <ColorSwatch color="#007AFF" name="Info"     description="Information" />
                  </SimpleGrid>
                </LightCard>

                {/* Quick Reference */}
                <LightCard>
                  <SectionHeader icon={FaLightbulb} title="Quick Reference" subtitle="Click any row to copy the value" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                    {[
                      { label: "Master Navy",         value: NAVY },
                      { label: "Navy Dark",           value: NAVY_DARK },
                      { label: "Navy Overlay 92%",    value: NAVY_OVR92 },
                      { label: "Rich Solar Gold",     value: SOLAR },
                      { label: "Warm Amber",          value: SOLAR_2 },
                      { label: "Burnt Bronze",        value: SOLAR_ACC },
                      { label: "Slate Graphite",      value: FINANCE },
                      { label: "Steel Grey",          value: FINANCE_2 },
                      { label: "Platinum",            value: FINANCE_ACC },
                      { label: "Heritage Green",      value: PROP },
                      { label: "Sage Growth",         value: PROP_2 },
                      { label: "Warm Sand",           value: PROP_ACC },
                      { label: "Mineral Blue",        value: WATER },
                      { label: "Deep Aqua",           value: WATER_2 },
                      { label: "Ice Mist",            value: WATER_ACC },
                    ].map((item) => (
                      <HStack
                        key={item.label} p={3} bg="gray.50" borderRadius="lg" justify="space-between"
                        cursor="pointer" border="1px solid" borderColor="gray.200" _hover={{ bg: "gray.100" }}
                        onClick={() => {
                          navigator.clipboard.writeText(item.value);
                          toast({ title: "Copied!", description: item.value, status: "success", duration: 1500 });
                        }}
                      >
                        <HStack>
                          <Box w="20px" h="20px" bg={item.value} borderRadius="md" border="1px solid" borderColor="gray.300" />
                          <Text color="gray.700" fontSize="sm">{item.label}</Text>
                        </HStack>
                        <Code fontSize="xs" bg="gray.100" color="gray.600" px={2}>{item.value}</Code>
                      </HStack>
                    ))}
                  </SimpleGrid>
                </LightCard>

              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 2 — TYPOGRAPHY
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">
                <LightCard>
                  <SectionHeader icon={FaFont} title="Typography System" subtitle="Montserrat headings · Open Sans body" color={NAVY} />

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
                    <Box p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color="gray.500" fontSize="xs" mb={3} textTransform="uppercase" letterSpacing="wider">Headings</Text>
                      <Text fontSize="3xl" fontWeight="bold" color={NAVY} fontFamily="Montserrat, sans-serif">Montserrat Bold</Text>
                      <Text fontSize="sm" color="gray.500" mt={2}>Used for H1–H4, display headings, campaign hooks</Text>
                      <Code fontSize="xs" bg="gray.100" color="gray.600" mt={3} p={2} borderRadius="md" display="block">
                        Montserrat Bold / SemiBold
                      </Code>
                    </Box>
                    <Box p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color="gray.500" fontSize="xs" mb={3} textTransform="uppercase" letterSpacing="wider">Body</Text>
                      <Text fontSize="lg" color="gray.700" fontFamily="'Open Sans', sans-serif">Open Sans Regular</Text>
                      <Text fontSize="sm" color="gray.500" mt={2}>Used for all body copy, form labels, descriptions</Text>
                      <Code fontSize="xs" bg="gray.100" color="gray.600" mt={3} p={2} borderRadius="md" display="block">
                        Open Sans Regular / Condensed (hooks only)
                      </Code>
                    </Box>
                  </SimpleGrid>

                  <Text color="gray.500" fontSize="sm" mb={4} textTransform="uppercase" letterSpacing="wider">Type Scale</Text>
                  <VStack spacing={4} align="stretch">
                    {[
                      { size: "4xl" as const, weight: "bold",    family: "Montserrat, sans-serif", label: "Display", sample: "Building The Prosperity Engine™" },
                      { size: "3xl" as const, weight: "bold",    family: "Montserrat, sans-serif", label: "H1",      sample: "One Group Australia" },
                      { size: "2xl" as const, weight: "bold",    family: "Montserrat, sans-serif", label: "H2",      sample: "From Health to Wealth" },
                      { size: "xl"  as const, weight: "semibold",family: "Montserrat, sans-serif", label: "H3",      sample: "The Prosperity Engine™ Framework" },
                      { size: "lg"  as const, weight: "medium",  family: "'Open Sans', sans-serif",label: "H4",      sample: "Structured. Calm. Intentional. Long-term." },
                      { size: "md"  as const, weight: "normal",  family: "'Open Sans', sans-serif",label: "Body",    sample: "One Group Australia designs life infrastructure through strategic integration." },
                      { size: "sm"  as const, weight: "normal",  family: "'Open Sans', sans-serif",label: "Small",   sample: "General Advice Warning. Past performance is not an indicator of future results." },
                    ].map((item, idx) => (
                      <HStack
                        key={idx} p={4} bg="gray.50" borderRadius="xl" justify="space-between"
                        align="center" border="1px solid" borderColor="gray.200"
                      >
                        <HStack spacing={4}>
                          <Badge bg={NAVY} color="white" px={3} py={1} borderRadius="md" minW="60px" textAlign="center">{item.label}</Badge>
                          <Text fontSize={item.size} fontWeight={item.weight} fontFamily={item.family} color="gray.800" noOfLines={1} maxW="460px">
                            {item.sample}
                          </Text>
                        </HStack>
                        <Code fontSize="xs" bg="gray.100" color="gray.500" px={2}>{item.size} / {item.weight}</Code>
                      </HStack>
                    ))}
                  </VStack>
                </LightCard>

                <LightCard>
                  <SectionHeader icon={FaPalette} title="ALL CAPS Policy" subtitle="Short hooks only — never full body copy" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#30D158"><Icon as={FaCheck} boxSize={3} color="white" /></Circle>
                        <Text color="gray.800" fontWeight="bold">ALLOWED</Text>
                      </HStack>
                      <VStack align="start" spacing={2}>
                        {["Short campaign hooks", "Campaign graphics", "SWITCH. ADD. UPGRADE.", "Section labels (e.g. OUR SERVICES)"].map((s, i) => (
                          <HStack key={i}><Icon as={FaCheck} color="#30D158" boxSize={3} /><Text color="gray.600" fontSize="sm">{s}</Text></HStack>
                        ))}
                      </VStack>
                    </Box>
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#FF3B30"><Text color="white" fontWeight="bold" fontSize="sm">✕</Text></Circle>
                        <Text color="gray.800" fontWeight="bold">NOT ALLOWED</Text>
                      </HStack>
                      <VStack align="start" spacing={2}>
                        {["Full email bodies", "Finance educational copy", "Water blog content", "Long-form paragraphs"].map((s, i) => (
                          <HStack key={i}><Text color="#FF3B30" fontWeight="bold">✕</Text><Text color="gray.600" fontSize="sm">{s}</Text></HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </LightCard>
              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 3 — COMPONENTS
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">

                {/* Buttons */}
                <LightCard>
                  <SectionHeader icon={FaSquare} title="Button Hierarchy" subtitle="Navy dominant · Division colour for CTAs" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <Box>
                      <Text color="gray.500" fontSize="sm" mb={4} textTransform="uppercase" letterSpacing="wider">Primary Buttons</Text>
                      <VStack spacing={4} align="stretch">
                        <Button bg={NAVY}    color="white" size="lg" _hover={{ bg: NAVY_DARK }}   rightIcon={<FaArrowRight />}>Book Consultation — Navy</Button>
                        <Button bg={SOLAR}   color="white" size="lg" _hover={{ bg: SOLAR_DARK }}  rightIcon={<FaArrowRight />}>Get a Solar Quote</Button>
                        <Button bg={FINANCE} color="white" size="lg" _hover={{ bg: FINANCE_DARK }}rightIcon={<FaArrowRight />}>Structured for What's Next</Button>
                        <Button bg={PROP}    color="white" size="lg" _hover={{ bg: PROP_DARK }}   rightIcon={<FaArrowRight />}>Build Your Legacy</Button>
                        <Button bg={WATER}   color="white" size="lg" _hover={{ bg: WATER_DARK }}  rightIcon={<FaArrowRight />}>Know Your Water</Button>
                      </VStack>
                    </Box>
                    <Box>
                      <Text color="gray.500" fontSize="sm" mb={4} textTransform="uppercase" letterSpacing="wider">Outline / Ghost</Text>
                      <VStack spacing={4} align="stretch">
                        <Button variant="outline" borderColor={NAVY}  color={NAVY}  size="lg" _hover={{ bg: "gray.100" }}>View Services</Button>
                        <Button variant="outline" borderColor={SOLAR} color={SOLAR} size="lg" _hover={{ bg: `${SOLAR}10` }}>Calculate Savings</Button>
                        <Button variant="ghost"   color={NAVY}        size="lg"     rightIcon={<FaArrowRight />} _hover={{ bg: "gray.100" }}>Learn More →</Button>
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                {/* Badges */}
                <LightCard>
                  <SectionHeader icon={FaCircle} title="Badges & Division Tags" subtitle="Division identification across all assets" color={NAVY} />
                  <Wrap spacing={4}>
                    {[
                      { label: "One Group Australia", bg: NAVY },
                      { label: "One Solar Australia",    bg: SOLAR },
                      { label: "One Finance Australia",  bg: FINANCE },
                      { label: "One Property Australia", bg: PROP },
                      { label: "One Water Australia",    bg: WATER },
                      { label: "Prosperity Engine™",     bg: NAVY },
                    ].map((b) => (
                      <WrapItem key={b.label}>
                        <Badge bg={b.bg} color="white" px={3} py={1} borderRadius="full">{b.label}</Badge>
                      </WrapItem>
                    ))}
                    {[
                      { label: "Solar", border: SOLAR, color: SOLAR },
                      { label: "Finance", border: FINANCE, color: FINANCE },
                      { label: "Property", border: PROP, color: PROP },
                      { label: "Water", border: WATER, color: WATER },
                    ].map((b) => (
                      <WrapItem key={b.label}>
                        <Badge variant="outline" borderColor={b.border} color={b.color} px={3} py={1} borderRadius="full">{b.label}</Badge>
                      </WrapItem>
                    ))}
                  </Wrap>
                </LightCard>

                {/* Division Cards */}
                <LightCard>
                  <SectionHeader icon={FaSquare} title="Division Cards" subtitle="Content containers — left-aligned, generous whitespace" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {[
                      { division: "One Solar Australia",    tagline: "SWITCH. ADD. UPGRADE.",            color: SOLAR,   icon: FaSun,      desc: "Power your home with Rich Solar Gold." },
                      { division: "One Finance Australia",  tagline: "Structured for what's next.",      color: FINANCE, icon: FaCoins,    desc: "Discipline. Structure. Clarity." },
                      { division: "One Property Australia", tagline: "Real people. Real conversations.",  color: PROP,    icon: FaHome,     desc: "Heritage Green — growth and legacy." },
                      { division: "One Water Australia",    tagline: "Know what's in your water.",       color: WATER,   icon: FaTint,     desc: "Mineral Blue — clarity and purity." },
                    ].map((card) => (
                      <Box
                        key={card.division} p={6} bg="white" borderRadius="xl"
                        border="1px solid" borderColor="gray.200" boxShadow="sm"
                        borderLeft="4px solid" borderLeftColor={card.color}
                        _hover={{ boxShadow: "lg", borderLeftColor: card.color }}
                        transition="all 0.3s"
                      >
                        <HStack mb={3}>
                          <Circle size="40px" bg={`${card.color}15`}><Icon as={card.icon} color={card.color} /></Circle>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="700" color="gray.800" fontSize="sm">{card.division}</Text>
                            <Text fontSize="xs" color={card.color} fontWeight="600">{card.tagline}</Text>
                          </VStack>
                        </HStack>
                        <Text color="gray.600" fontSize="sm">{card.desc}</Text>
                        <Button mt={4} size="sm" bg={card.color} color="white" _hover={{ opacity: 0.9 }}>Explore →</Button>
                      </Box>
                    ))}
                  </SimpleGrid>
                </LightCard>

                {/* Form Elements */}
                <LightCard>
                  <SectionHeader icon={FaSquare} title="Form Elements" subtitle="Clean, white-first, navy focus states" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <VStack spacing={4} align="stretch">
                      <FormControl>
                        <FormLabel color="gray.700">Full Name</FormLabel>
                        <Input placeholder="Monica Smith" bg="white" border="1px solid" borderColor="gray.300" color="gray.800"
                          _focus={{ borderColor: NAVY, boxShadow: `0 0 0 1px ${NAVY}` }} />
                      </FormControl>
                      <FormControl>
                        <FormLabel color="gray.700">Investment Goal</FormLabel>
                        <Input placeholder="e.g. Retire at 55 with $3M portfolio" bg="white" border="1px solid" borderColor="gray.300"
                          _focus={{ borderColor: PROP, boxShadow: `0 0 0 1px ${PROP}` }} />
                      </FormControl>
                    </VStack>
                    <VStack spacing={4} align="stretch">
                      <FormControl display="flex" alignItems="center">
                        <FormLabel color="gray.700" mb={0}>Receive insights</FormLabel>
                        <Switch colorScheme="blue" />
                      </FormControl>
                      <Progress value={72} size="lg" borderRadius="full" bg="gray.200"
                        sx={{ "& > div": { background: `linear-gradient(90deg, ${NAVY} 0%, ${PROP} 100%)` } }} />
                      <Text color="gray.500" fontSize="sm">Portfolio Progress: 72%</Text>
                    </VStack>
                  </SimpleGrid>
                </LightCard>

                {/* Alerts */}
                <LightCard>
                  <SectionHeader icon={FaEye} title="Alerts & Notifications" subtitle="System messages — clear, calm, no hype" color={NAVY} />
                  <VStack spacing={4} align="stretch">
                    <Alert status="success" borderRadius="xl" bg="green.50" border="1px solid" borderColor="green.200">
                      <AlertIcon color="#30D158" /><Text color="gray.700">Your consultation has been confirmed.</Text>
                    </Alert>
                    <Alert status="info" borderRadius="xl" bg="blue.50" border="1px solid" borderColor="blue.200">
                      <AlertIcon color="#007AFF" /><Text color="gray.700">Pre-approval assessment typically takes 24–48 hours.</Text>
                    </Alert>
                    <Alert status="warning" borderRadius="xl" bg="orange.50" border="1px solid" borderColor="orange.200">
                      <AlertIcon color="#FF9F0A" /><Text color="gray.700">Please complete your asset profile to continue.</Text>
                    </Alert>
                    <Alert status="error" borderRadius="xl" bg="red.50" border="1px solid" borderColor="red.200">
                      <AlertIcon color="#FF3B30" /><Text color="gray.700">Unable to process submission. Please try again.</Text>
                    </Alert>
                  </VStack>
                </LightCard>

              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 4 — PROSPERITY ENGINE
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">
                <LightCard>
                  <SectionHeader icon={FaLayerGroup} title="The Prosperity Engine™" subtitle="Four strategic levers — one coordinated system" color={NAVY} />

                  {/* Flow */}
                  <Box bg={NAVY} borderRadius="2xl" p={8} mb={8}>
                    <Text color="white" fontWeight="700" textAlign="center" fontSize="lg" mb={4}>
                      The Prosperity Engine Flow
                    </Text>
                    <HStack justify="center" spacing={2} flexWrap="wrap">
                      {["Health", "→", "Stability", "→", "Structure", "→", "Growth", "→", "Legacy"].map((item, i) => (
                        <Text key={i} color={item === "→" ? "gray.400" : SOLAR} fontWeight={item === "→" ? "normal" : "bold"} fontSize="lg">
                          {item}
                        </Text>
                      ))}
                    </HStack>
                  </Box>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {[
                      {
                        icon: FaTint, color: WATER, division: "One Water Australia", lever: "💧 Health (Water)",
                        desc: "Water protects wellbeing. Clean water is the foundation of a healthy, high-performing life.",
                        tagline: "Know what's in your water.",
                      },
                      {
                        icon: FaSun, color: SOLAR, division: "One Solar Australia", lever: "☀️ Efficiency (Solar)",
                        desc: "Solar strengthens cash flow. Energy independence creates financial headroom to invest.",
                        tagline: "SWITCH. ADD. UPGRADE.",
                      },
                      {
                        icon: FaCoins, color: FINANCE, division: "One Finance Australia", lever: "💰 Structure (Finance)",
                        desc: "Finance optimises architecture. Structured borrowing and wealth planning unlock compounding.",
                        tagline: "Structured for what's next.",
                      },
                      {
                        icon: FaHome, color: PROP, division: "One Property Australia", lever: "🏡 Growth (Property)",
                        desc: "Property builds appreciating assets. Strategic acquisition creates intergenerational legacy.",
                        tagline: "Real people. Real conversations. Real finance.",
                      },
                    ].map((card) => (
                      <Box key={card.division} p={6} borderRadius="xl" border="2px solid" borderColor={card.color}
                        bg={`linear-gradient(135deg, ${card.color}08 0%, white 100%)`}>
                        <HStack mb={3}>
                          <Circle size="44px" bg={card.color}><Icon as={card.icon} color="white" /></Circle>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="700" color={NAVY}>{card.lever}</Text>
                            <Text fontSize="xs" color={card.color} fontWeight="600">{card.division}</Text>
                          </VStack>
                        </HStack>
                        <Text color="gray.700" fontSize="sm" mb={3}>{card.desc}</Text>
                        <Code fontSize="xs" bg={`${card.color}15`} color={card.color} px={2} py={1} borderRadius="md">
                          {card.tagline}
                        </Code>
                      </Box>
                    ))}
                  </SimpleGrid>

                  <Divider my={8} borderColor="gray.200" />
                  <Heading size="md" color="gray.800" mb={4}>What The Engine Creates</Heading>
                  <SimpleGrid columns={{ base: 2, md: 5 }} spacing={4}>
                    {["Financial Strength", "Physical Wellbeing", "Emotional Stability", "Long-Term Security", "Intergenerational Legacy"].map((outcome) => (
                      <Box key={outcome} p={4} bg={NAVY} borderRadius="xl" textAlign="center">
                        <Text color="white" fontWeight="600" fontSize="sm">{outcome}</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </LightCard>

                <LightCard>
                  <SectionHeader icon={FaShieldAlt} title="Brand Equity Strategy" subtitle="Multi-decade authority — consistency over promotion" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <VStack align="start" spacing={4}>
                      {[
                        { label: "Become synonymous with structured prosperity" },
                        { label: "Lead with education before selling" },
                        { label: "Build cross-division lifetime customers" },
                        { label: "Maintain tonal discipline across all assets" },
                        { label: "Avoid short-term hype for long-term authority" },
                      ].map((item, i) => (
                        <HStack key={i}>
                          <Circle size="24px" bg={NAVY}><Text color="white" fontSize="xs" fontWeight="bold">{i + 1}</Text></Circle>
                          <Text color="gray.700" fontSize="sm">{item.label}</Text>
                        </HStack>
                      ))}
                    </VStack>
                    <Box p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <Text color="gray.500" fontSize="xs" mb={4} textTransform="uppercase" letterSpacing="wider">Brand Equity is Built Through</Text>
                      {["Consistency", "Clarity", "Calm authority", "Strategic integration", "Reputation over promotion"].map((v) => (
                        <HStack key={v} mb={2}>
                          <Box w={2} h={2} borderRadius="full" bg={SOLAR} />
                          <Text color="gray.700" fontSize="sm" fontWeight="500">{v}</Text>
                        </HStack>
                      ))}
                    </Box>
                  </SimpleGrid>
                </LightCard>

              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 5 — ARCHETYPES
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">

                {/* Group Archetypes */}
                <LightCard>
                  <SectionHeader icon={FaGraduationCap} title="One Group Australia — Brand Archetypes" subtitle="The Architect · The Guardian · The Builder" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={8}>
                    {[
                      { role: "Primary", archetype: "The Architect", desc: "Designs systems. Sees the full structure. Thinks long-term.", traits: ["Systems thinker", "Strategic", "Long-term", "Precise"] },
                      { role: "Secondary", archetype: "The Guardian", desc: "Protects families from inefficiency and misinformation.", traits: ["Trusted", "Protective", "Honest", "Reliable"] },
                      { role: "Tertiary", archetype: "The Builder", desc: "Creates tangible outcomes over decades.", traits: ["Hands-on", "Persistent", "Results-driven", "Legacy-focused"] },
                    ].map((a) => (
                      <Box key={a.archetype} p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                        <Badge bg={NAVY} color="white" mb={3} px={3} py={1} borderRadius="full" fontSize="xs">{a.role}</Badge>
                        <Heading size="md" color={NAVY} mb={2}>{a.archetype}</Heading>
                        <Text color="gray.600" fontSize="sm" mb={4}>{a.desc}</Text>
                        <Wrap spacing={2}>
                          {a.traits.map((t) => <WrapItem key={t}><Badge bg="gray.200" color="gray.700" px={2} py={1} borderRadius="md" fontSize="xs">{t}</Badge></WrapItem>)}
                        </Wrap>
                      </Box>
                    ))}
                  </SimpleGrid>
                </LightCard>

                {/* Division Archetypes */}
                <LightCard>
                  <SectionHeader icon={FaLeaf} title="Division Archetypes" subtitle="Each division has its own mood and energy" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {[
                      {
                        division: "One Solar Australia", icon: FaSun, color: SOLAR,
                        archetype: "The Empowerer / Hero", mood: "Confident, energetic, control-oriented", energy: "Medium-High",
                        traits: ["Empowering", "Confident", "Action-oriented", "Bold"],
                      },
                      {
                        division: "One Finance Australia", icon: FaCoins, color: FINANCE,
                        archetype: "The Strategist / Architect", mood: "Structured, intelligent, composed", energy: "Medium-Low",
                        traits: ["Analytical", "Measured", "Strategic", "Composed"],
                      },
                      {
                        division: "One Property Australia", icon: FaHome, color: PROP,
                        archetype: "The Builder / Visionary", mood: "Aspirational, grounded, legacy-focused", energy: "Medium",
                        traits: ["Aspirational", "Grounded", "Long-term", "Legacy"],
                      },
                      {
                        division: "One Water Australia", icon: FaTint, color: WATER,
                        archetype: "The Sage / Guardian", mood: "Calm, research-backed, transparent", energy: "Low",
                        traits: ["Calm", "Evidence-based", "Transparent", "Protective"],
                      },
                    ].map((div) => (
                      <Box key={div.division} p={6} borderRadius="xl" border="1px solid" borderColor="gray.200"
                        borderLeft="4px solid" borderLeftColor={div.color}>
                        <HStack mb={3}>
                          <Circle size="40px" bg={div.color}><Icon as={div.icon} color="white" /></Circle>
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="700" color="gray.800" fontSize="sm">{div.division}</Text>
                            <Text fontSize="xs" color={div.color} fontWeight="600">{div.archetype}</Text>
                          </VStack>
                        </HStack>
                        <Text color="gray.600" fontSize="sm" mb={1}><strong>Mood:</strong> {div.mood}</Text>
                        <Text color="gray.600" fontSize="sm" mb={3}><strong>Energy:</strong> {div.energy}</Text>
                        <Wrap spacing={2}>
                          {div.traits.map((t) => <WrapItem key={t}><Badge bg={`${div.color}20`} color={div.color} px={2} borderRadius="md" fontSize="xs">{t}</Badge></WrapItem>)}
                        </Wrap>
                      </Box>
                    ))}
                  </SimpleGrid>
                </LightCard>

                {/* Audience Avatars */}
                <LightCard>
                  <SectionHeader icon={FaHeart} title="Audience Avatars" subtitle="Who we serve — tailored messaging per division" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    {[
                      { division: "One Water", color: WATER, avatars: [
                        { name: "Health-Conscious Parent", age: "30–45", desc: "Wants safe inputs for family. Values research-backed content." },
                        { name: "Research-Driven Homeowner", age: "35–55", desc: "Analytical. Wants clarity around monitoring gaps." },
                      ]},
                      { division: "One Solar", color: SOLAR, avatars: [
                        { name: "Bill-Frustrated Homeowner", age: "38–55", desc: "Mortgage holder. Feels rising electricity pressure." },
                        { name: "Smart Tech Upgrader", age: "35–50", desc: "Interested in batteries. Understands the 6pm usage problem." },
                      ]},
                      { division: "One Finance", color: FINANCE, avatars: [
                        { name: "High-Income, Poor-Structure Professional", age: "30–57", desc: "Combined income $250k+. Uncertain about offset vs redraw." },
                        { name: "SMSF-Curious Investor", age: "40–60", desc: "Combined super $250k+. Wants compliance clarity." },
                      ]},
                      { division: "One Property", color: PROP, avatars: [
                        { name: "Aspiring Legacy Builder", age: "35–57", desc: "Wants generational wealth. Has equity but no strategy." },
                        { name: "Strategic SMSF Investor", age: "42–62", desc: "Compliance-focused. Long-term thinker." },
                      ]},
                    ].map((d) => (
                      <Box key={d.division} p={6} borderRadius="xl" bg="gray.50" border="1px solid" borderColor="gray.200">
                        <Text fontWeight="700" color={d.color} mb={4} fontSize="sm">{d.division}</Text>
                        {d.avatars.map((a) => (
                          <Box key={a.name} mb={3} p={3} bg="white" borderRadius="lg" border="1px solid" borderColor="gray.100">
                            <Text fontWeight="600" color="gray.800" fontSize="sm">{a.name}</Text>
                            <Text fontSize="xs" color="gray.500">Age {a.age}</Text>
                            <Text fontSize="xs" color="gray.600" mt={1}>{a.desc}</Text>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </SimpleGrid>
                </LightCard>

              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 6 — VOICE & RULES
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={10} align="stretch">

                <LightCard>
                  <SectionHeader icon={FaBook} title="Tone of Voice Framework" subtitle="Measured · Calm · Authoritative · Long-term" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} mb={8}>
                    {[
                      { division: "One Water Australia",    tone: "Educational, calm, referenced",      color: WATER },
                      { division: "One Solar Australia",    tone: "Empowering, confident",              color: SOLAR },
                      { division: "One Finance Australia",  tone: "Strategic, measured, composed",      color: FINANCE },
                      { division: "One Property Australia", tone: "Conversational but grounded",        color: PROP },
                    ].map((t) => (
                      <HStack key={t.division} p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                        <Box w={3} h={12} bg={t.color} borderRadius="full" flexShrink={0} />
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="600" color="gray.800" fontSize="sm">{t.division}</Text>
                          <Text fontSize="xs" color="gray.500">{t.tone}</Text>
                        </VStack>
                      </HStack>
                    ))}
                  </SimpleGrid>

                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#30D158"><Icon as={FaCheck} boxSize={3} color="white" /></Circle>
                        <Text color="gray.800" fontWeight="bold">DO</Text>
                      </HStack>
                      <VStack spacing={3} align="start">
                        {[
                          "Use data and research to support claims",
                          "Speak with measured, calm confidence",
                          "Emphasise long-term outcomes",
                          "Lead with education before selling",
                          "Focus on client education and clarity",
                          "Be direct — structured and intentional",
                        ].map((item, i) => (
                          <HStack key={i}><Icon as={FaCheck} color="#30D158" boxSize={3} /><Text color="gray.600" fontSize="sm">{item}</Text></HStack>
                        ))}
                      </VStack>
                    </Box>
                    <Box>
                      <HStack mb={4}>
                        <Circle size="30px" bg="#FF3B30"><Text color="white" fontWeight="bold" fontSize="sm">✕</Text></Circle>
                        <Text color="gray.800" fontWeight="bold">NEVER</Text>
                      </HStack>
                      <VStack spacing={3} align="start">
                        {[
                          "Fear-based language",
                          "Hype-driven or aggressive copy",
                          '"Get rich quick" promises',
                          "Overpromising outcomes",
                          "Aggressive urgency or pressure tactics",
                          "Mixed division colours on one asset",
                        ].map((item, i) => (
                          <HStack key={i}><Text color="#FF3B30" fontWeight="bold">✕</Text><Text color="gray.600" fontSize="sm">{item}</Text></HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </LightCard>

                <LightCard>
                  <SectionHeader icon={FaShieldAlt} title="Brand Discipline Rules" subtitle="Every asset must pass this checklist" color={NAVY} />
                  <VStack spacing={4} align="stretch">
                    {[
                      { q: "Is this structured?",                         pass: "Clear hierarchy, left-aligned, strong whitespace." },
                      { q: "Is this premium?",                            pass: "Navy dominant, no clutter, Montserrat headings." },
                      { q: "Is it aligned with The Prosperity Engine™?",  pass: "Connects to Health → Stability → Structure → Growth → Legacy." },
                      { q: "Does it represent long-term thinking?",       pass: "No hype, no pressure — calm authority." },
                      { q: "Is navy 50–60% of the composition?",          pass: "Background, headers, or section dividers use #141C49." },
                      { q: "Is the correct division colour in use?",      pass: "One colour system per piece. No mixing." },
                    ].map((rule, i) => (
                      <HStack key={i} p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200" align="start">
                        <Circle size="28px" bg={NAVY} flexShrink={0}><Text color="white" fontSize="xs" fontWeight="bold">{i + 1}</Text></Circle>
                        <VStack align="start" spacing={0}>
                          <Text fontWeight="600" color="gray.800" fontSize="sm">{rule.q}</Text>
                          <Text fontSize="xs" color="gray.500">{rule.pass}</Text>
                        </VStack>
                      </HStack>
                    ))}
                  </VStack>
                </LightCard>

                <LightCard>
                  <SectionHeader icon={FaBook} title="Tagline System" subtitle="Master tagline + division taglines" color={NAVY} />
                  <VStack spacing={4} align="stretch">
                    <Box p={5} bg={NAVY} borderRadius="xl">
                      <Text color={SOLAR} fontSize="xs" fontWeight="700" mb={1} textTransform="uppercase" letterSpacing="wider">One Group Australia</Text>
                      <Text color="white" fontSize="lg" fontWeight="700">Building The Prosperity Engine™ — From Health to Wealth</Text>
                    </Box>
                    {[
                      { name: "One Solar Australia",    tagline: "SWITCH. ADD. UPGRADE. The Smart Choice.", color: SOLAR },
                      { name: "One Finance Australia",  tagline: "Structured for what's next.",             color: FINANCE },
                      { name: "One Property Australia", tagline: "Real people. Real conversations. Real finance.", color: PROP },
                      { name: "One Water Australia",    tagline: "Know what's in your water.",              color: WATER },
                    ].map((t) => (
                      <HStack key={t.name} p={4} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200" justify="space-between">
                        <Text fontWeight="600" color="gray.700" fontSize="sm">{t.name}</Text>
                        <Text color={t.color} fontWeight="600" fontSize="sm" fontStyle="italic">{t.tagline}</Text>
                      </HStack>
                    ))}
                  </VStack>
                </LightCard>

                <LightCard>
                  <SectionHeader icon={FaEye} title="Logo System" subtitle="The circular O — unity and The Prosperity Engine™" color={NAVY} />
                  <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                    <Box p={6} bg="white" borderRadius="xl" textAlign="center" border="1px solid" borderColor="gray.200">
                      <Box as="img" src={brandConfig.logo.src} alt={brandConfig.logo.alt} maxH="60px" mx="auto" mb={4} />
                      <Text color="gray.600" fontSize="sm">Primary — On White</Text>
                      <Code fontSize="xs" bg="gray.100" color="gray.500" mt={1}>#FFFFFF</Code>
                    </Box>
                    <Box p={6} bg={NAVY} borderRadius="xl" textAlign="center">
                      <Box as="img" src={brandConfig.logo.src} alt={brandConfig.logo.alt} maxH="60px" mx="auto" mb={4} />
                      <Text color="white" fontSize="sm">Primary — On Navy</Text>
                      <Code fontSize="xs" bg="whiteAlpha.200" color="gray.300" mt={1}>{NAVY}</Code>
                    </Box>
                    <Box p={6} bg="gray.50" borderRadius="xl" border="1px solid" borderColor="gray.200">
                      <VStack spacing={2} align="start">
                        <Text color="gray.500" fontSize="xs" fontWeight="600" textTransform="uppercase">Logo Rules</Text>
                        {["Never separate O and ONE", "Clear space = 50% of O height", "No distortion of proportions", "Division variation via colour only"].map((r) => (
                          <HStack key={r}><Icon as={FaCheck} color={PROP} boxSize={3} /><Text fontSize="xs" color="gray.600">{r}</Text></HStack>
                        ))}
                      </VStack>
                    </Box>
                  </SimpleGrid>
                </LightCard>

              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 7 — SOLAR PREVIEW
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                <Box position="relative" py={20} overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1509391366360-2e959784a276?w=1920&q=80')"
                  backgroundSize="cover" backgroundPosition="center"
                  _before={{ content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bg: NAVY_OVR88 }}>
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={SOLAR} boxShadow={`0 8px 30px ${SOLAR}60`}>
                        <Icon as={FaSun} boxSize={10} color="white" />
                      </Circle>
                      <Badge bg={SOLAR} color="white" px={4} py={2} borderRadius="full" fontSize="sm">One Solar Australia</Badge>
                      <Heading size="3xl" color="white" fontWeight="black">SWITCH. ADD. UPGRADE.</Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">The Smart Choice. Australian-owned solar solutions for homes, businesses, and investment properties.</Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={SOLAR} color="white" size="lg" _hover={{ bg: SOLAR_DARK }} rightIcon={<FaArrowRight />}>Get a Free Quote</Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>Calculate Savings</Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "2,500+", label: "Systems Installed" },
                        { value: "50MW+",  label: "Energy Generated" },
                        { value: "$15M+",  label: "Customer Savings" },
                        { value: "15,000t",label: "CO₂ Offset" },
                      ].map((s, i) => (
                        <VStack key={i}>
                          <Text fontSize="4xl" fontWeight="bold" color={SOLAR}>{s.value}</Text>
                          <Text color="gray.600">{s.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={SOLAR_ACC} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">The Smart Choice</Text>
                      <Heading size="xl" color="gray.800">Solar for Every Need</Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                      {[
                        { title: "Residential Solar",      desc: "Custom-designed systems for homes of all sizes", icon: FaHome },
                        { title: "Commercial Solar",       desc: "Large-scale solutions for business energy needs", icon: FaLightbulb },
                        { title: "Investment Properties",  desc: "Add value and rental appeal to your portfolio",  icon: FaCoins },
                      ].map((s, i) => (
                        <Box key={i} p={8} bg="white" borderRadius="2xl" border="2px solid" borderColor={SOLAR}
                          textAlign="center" _hover={{ transform: "translateY(-4px)", shadow: "xl" }} transition="all 0.3s">
                          <Circle size="60px" bg={`${SOLAR}15`} mx="auto" mb={4}><Icon as={s.icon} boxSize={6} color={SOLAR} /></Circle>
                          <Heading size="md" color="gray.800" mb={2}>{s.title}</Heading>
                          <Text color="gray.600" fontSize="sm">{s.desc}</Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg={NAVY} py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">Ready to Start Saving?</Heading>
                      <Text color="gray.300" maxW="xl">Get a free, no-obligation quote and see how much you could save with solar.</Text>
                      <Button bg={SOLAR} color="white" size="lg" _hover={{ bg: SOLAR_DARK }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 8 — FINANCE PREVIEW
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                <Box position="relative" py={20} overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1554224155-6726b3ff858f?w=1920&q=80')"
                  backgroundSize="cover" backgroundPosition="center"
                  _before={{ content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bg: NAVY_OVR88 }}>
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={FINANCE} boxShadow={`0 8px 30px ${FINANCE}60`}>
                        <Icon as={FaCoins} boxSize={10} color={FINANCE_ACC} />
                      </Circle>
                      <Badge bg={FINANCE} color={FINANCE_ACC} px={4} py={2} borderRadius="full" fontSize="sm">One Finance Australia</Badge>
                      <Heading size="3xl" color="white" fontWeight="black">Structured for What's Next.</Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">From first home buyers to wealth building, we architect financial structures that create lasting prosperity.</Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={FINANCE} color={FINANCE_ACC} size="lg" _hover={{ bg: FINANCE_DARK }} rightIcon={<FaArrowRight />}>Get Started</Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>Book Consultation</Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "$2B+",    label: "Loans Settled",    sub: "Since establishment" },
                        { value: "40+",     label: "Lender Partners",  sub: "Best rates" },
                        { value: "5,000+",  label: "Clients Advised",  sub: "5-star reviews" },
                        { value: "48hrs",   label: "Pre-Approval",     sub: "Average turnaround" },
                      ].map((s, i) => (
                        <VStack key={i}>
                          <Text fontSize="4xl" fontWeight="bold" color={FINANCE}>{s.value}</Text>
                          <Text color="gray.800" fontWeight="600">{s.label}</Text>
                          <Text color="gray.500" fontSize="sm">{s.sub}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={FINANCE} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">Our Services</Text>
                      <Heading size="xl" color="gray.800">Comprehensive Finance Solutions</Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                      {[
                        { title: "Home Loans", desc: "First homes, upgrades, refinancing & investment loans", features: ["40+ lenders", "Best rates", "Fast approval"] },
                        { title: "Wealth Management", desc: "Strategic planning for long-term financial security", features: ["Tax strategies", "Portfolio review", "Retirement planning"] },
                        { title: "Commercial Finance", desc: "Business loans, equipment finance & cash flow solutions", features: ["Flexible terms", "Quick decisions", "Expert advice"] },
                        { title: "Specialist Finance", desc: "SMSF, self-employed & complex situations", features: ["SMSF lending", "No-doc options", "Compliance clarity"] },
                      ].map((s, i) => (
                        <Box key={i} p={8} bg="white" borderRadius="2xl" border="1px solid" borderColor="gray.200"
                          boxShadow="sm" _hover={{ borderColor: FINANCE, shadow: "lg" }} transition="all 0.3s">
                          <Heading size="md" color="gray.800" mb={2}>{s.title}</Heading>
                          <Text color="gray.600" fontSize="sm" mb={4}>{s.desc}</Text>
                          <HStack spacing={2} flexWrap="wrap">
                            {s.features.map((f, fi) => (
                              <Badge key={fi} bg={`${FINANCE}15`} color={FINANCE} px={2} py={1} borderRadius="md" fontSize="xs">{f}</Badge>
                            ))}
                          </HStack>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg={NAVY} py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">Ready to Get Structured?</Heading>
                      <Text color="gray.300" maxW="xl">Book a free consultation and discover how we can architect your financial future.</Text>
                      <Button bg={FINANCE} color={FINANCE_ACC} size="lg" _hover={{ bg: FINANCE_DARK }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 9 — PROPERTY PREVIEW
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                <Box position="relative" py={20} overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=1920&q=80')"
                  backgroundSize="cover" backgroundPosition="center"
                  _before={{ content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bg: NAVY_OVR88 }}>
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={PROP} boxShadow={`0 8px 30px ${PROP}60`}>
                        <Icon as={FaHome} boxSize={10} color={PROP_ACC} />
                      </Circle>
                      <Badge bg={PROP} color={PROP_ACC} px={4} py={2} borderRadius="full" fontSize="sm">One Property Australia</Badge>
                      <Heading size="3xl" color="white" fontWeight="black">Real People. Real Conversations. Real Finance.</Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">
                        Has equity but no strategy? We help Australians aged 35–57 build generational wealth through disciplined property investment.
                      </Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={PROP} color={PROP_ACC} size="lg" _hover={{ bg: PROP_DARK }} rightIcon={<FaArrowRight />}>Start Your Journey</Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>Book Consultation</Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "$2.5M+", label: "Portfolio Value Managed" },
                        { value: "18.5%",  label: "Avg. Annual Return" },
                        { value: "150+",   label: "Properties Advised" },
                        { value: "95%",    label: "Client Satisfaction" },
                      ].map((s, i) => (
                        <VStack key={i}>
                          <Text fontSize="4xl" fontWeight="bold" color={PROP}>{s.value}</Text>
                          <Text color="gray.600">{s.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg="gray.50" py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={PROP_2} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">Our Approach</Text>
                      <Heading size="xl" color="gray.800">The Builder / Visionary Framework</Heading>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                      {[
                        { title: "Data-Driven Analysis",       desc: "Market trends, suburb comparison, and risk assessment to find the right opportunities", icon: FaEye },
                        { title: "Legacy-Focused Planning",    desc: "Personalised strategies with milestone tracking and intergenerational timeline", icon: FaGraduationCap },
                        { title: "Portfolio Diversification",  desc: "Risk mitigation through asset class mixing and geographic spread", icon: FaCircle },
                      ].map((p, i) => (
                        <Box key={i} p={8} bg="white" borderRadius="2xl" border="2px solid" borderColor={PROP}
                          textAlign="center" _hover={{ transform: "translateY(-4px)", shadow: "xl" }} transition="all 0.3s">
                          <Circle size="60px" bg={`${PROP}15`} mx="auto" mb={4}><Icon as={p.icon} boxSize={6} color={PROP} /></Circle>
                          <Heading size="md" color="gray.800" mb={2}>{p.title}</Heading>
                          <Text color="gray.600" fontSize="sm">{p.desc}</Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg={NAVY} py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">Ready to Build Your Legacy?</Heading>
                      <Text color="gray.300" maxW="xl">Book a consultation and discover how property investment can secure your family's future.</Text>
                      <Button bg={PROP} color={PROP_ACC} size="lg" _hover={{ bg: PROP_DARK }} rightIcon={<FaArrowRight />}>
                        Book Your Free Consultation
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

            {/* ══════════════════════════════════════════
                TAB 10 — WATER PREVIEW
            ══════════════════════════════════════════ */}
            <TabPanel px={0}>
              <VStack spacing={0} align="stretch">
                <Box position="relative" py={20} overflow="hidden"
                  backgroundImage="url('https://images.unsplash.com/photo-1559825481-12a05cc00344?w=1920&q=80')"
                  backgroundSize="cover" backgroundPosition="center"
                  _before={{ content: '""', position: "absolute", top: 0, left: 0, right: 0, bottom: 0, bg: NAVY_OVR88 }}>
                  <Container maxW="container.xl" position="relative" zIndex={1}>
                    <VStack spacing={6} textAlign="center">
                      <Circle size="80px" bg={WATER} boxShadow={`0 8px 30px ${WATER}60`}>
                        <Icon as={FaTint} boxSize={10} color="white" />
                      </Circle>
                      <Badge bg={WATER} color="white" px={4} py={2} borderRadius="full" fontSize="sm">One Water Australia</Badge>
                      <Heading size="3xl" color="white" fontWeight="black">Know What's In Your Water.</Heading>
                      <Text fontSize="xl" color="gray.300" maxW="2xl">
                        Research-backed water quality solutions for health-conscious Australian families. The foundation of The Prosperity Engine™.
                      </Text>
                      <HStack spacing={4} pt={4}>
                        <Button bg={WATER} color="white" size="lg" _hover={{ bg: WATER_DARK }} rightIcon={<FaArrowRight />}>Test Your Water</Button>
                        <Button variant="outline" borderColor="white" color="white" size="lg" _hover={{ bg: "whiteAlpha.200" }}>Learn More</Button>
                      </HStack>
                    </VStack>
                  </Container>
                </Box>
                <Box bg="white" py={12} borderBottom="1px solid" borderColor="gray.200">
                  <Container maxW="container.xl">
                    <SimpleGrid columns={{ base: 2, md: 4 }} spacing={8}>
                      {[
                        { value: "99.9%",   label: "Filtration Rate" },
                        { value: "10,000+", label: "Homes Protected" },
                        { value: "50+",     label: "Contaminants Removed" },
                        { value: "98%",     label: "Client Satisfaction" },
                      ].map((s, i) => (
                        <VStack key={i}>
                          <Text fontSize="4xl" fontWeight="bold" color={WATER}>{s.value}</Text>
                          <Text color="gray.600">{s.label}</Text>
                        </VStack>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg={WATER_ACC} py={16}>
                  <Container maxW="container.xl">
                    <VStack spacing={4} mb={12} textAlign="center">
                      <Text color={WATER_2} fontWeight="600" fontSize="sm" textTransform="uppercase" letterSpacing="wider">The Sage / Guardian</Text>
                      <Heading size="xl" color={NAVY}>Research-Backed Water Quality</Heading>
                      <Text color="gray.600" maxW="2xl">We are calm, transparent, and evidence-driven — not alarmist. Understanding precedes action.</Text>
                    </VStack>
                    <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                      {[
                        { title: "Home Filtration Systems", desc: "Under-bench and whole-house systems designed for Australian water conditions", icon: FaHome },
                        { title: "Water Quality Testing",   desc: "Comprehensive testing panels to understand exactly what's in your supply",  icon: FaEye },
                        { title: "Ongoing Monitoring",      desc: "Regular filter maintenance and quality checks for sustained protection",     icon: FaShieldAlt },
                      ].map((s, i) => (
                        <Box key={i} p={8} bg="white" borderRadius="2xl" border="2px solid" borderColor={WATER}
                          textAlign="center" _hover={{ transform: "translateY(-4px)", shadow: "xl" }} transition="all 0.3s">
                          <Circle size="60px" bg={`${WATER}20`} mx="auto" mb={4}><Icon as={s.icon} boxSize={6} color={WATER} /></Circle>
                          <Heading size="md" color="gray.800" mb={2}>{s.title}</Heading>
                          <Text color="gray.600" fontSize="sm">{s.desc}</Text>
                        </Box>
                      ))}
                    </SimpleGrid>
                  </Container>
                </Box>
                <Box bg={NAVY} py={16}>
                  <Container maxW="container.lg">
                    <VStack spacing={6} textAlign="center">
                      <Heading size="xl" color="white">Know What's In Your Water.</Heading>
                      <Text color="gray.300" maxW="xl">Book a free water quality consultation. Calm, evidence-based, transparent — no pressure.</Text>
                      <Button bg={WATER} color="white" size="lg" _hover={{ bg: WATER_DARK }} rightIcon={<FaArrowRight />}>
                        Book Your Water Test
                      </Button>
                    </VStack>
                  </Container>
                </Box>
              </VStack>
            </TabPanel>

          </TabPanels>
        </Tabs>
      </Container>
    </>
  );
};

export default BrandStyleGuideV2;
